/**
 * @module lib/storage
 * @description Safe JSON read/write over browser storage.
 *
 * Storage can be missing or throw (private mode, blocked site data, previews),
 * so every access is guarded and the app works without it. This is a
 * per-visitor convenience only, never state of record.
 */

/** Which browser store: "session" = this visit (Tier 1), "local" = across visits (Tier 2+). */
export type StoreKind = "session" | "local";

/** @returns the Storage object, or null when unavailable. */
function store(kind: StoreKind): Storage | null {
    try {
        return kind === "local" ? window.localStorage : window.sessionStorage;
    } catch {
        return null;
    }
}

/** @returns the parsed value, or `fallback` when missing, unreadable or malformed. */
export function readJson<T>(kind: StoreKind, key: string, fallback: T): T {
    try {
        const raw: string | null = store(kind)?.getItem(key) ?? null;
        return raw === null ? fallback : (JSON.parse(raw) as T);
    } catch {
        return fallback;
    }
}

/** Writes JSON; failures are ignored on purpose (convenience storage). */
export function writeJson(kind: StoreKind, key: string, value: unknown): void {
    try {
        store(kind)?.setItem(key, JSON.stringify(value));
    } catch {
        // Quota or privacy mode: the in-memory state still works.
    }
}

/** Storage keys, versioned so a shape change never reads stale data. */
export const STORAGE_KEYS = {
    cart: "afj-coc.cart.v1",
    saved: "afj-coc.saved.v1",
    donor: "afj-coc.donor.v1",
} as const;
